import PillButton from "client/common/components/buttons/PillButton";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import { urls } from "client/routes";
import { useProductLinesViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import { ProductLineListSource } from "client/analytics/types";

import CategoryCard from "./CategoryCard";
import CategoryCardV2 from "./CategoryCardV2";
import ProductLineCard, { ProductLineForCard } from "./ProductLineCard";
import { CarouselDisplayCategory, CarouselDisplayProductLine } from "../types";

export const toCardShape = (pl: CarouselDisplayProductLine): ProductLineForCard => ({
  ...pl.product_line,
  is_featured: pl.is_featured,
  is_loyalty_program_featured: pl.is_loyalty,
});

// Looked up by key from each caller's CSS module (which TS types as a string
// index signature), so we accept the same loose shape and read the keys we
// need at the call sites below.
export type CarouselSectionClassNames = Readonly<Record<string, string>>;

type TopSellerCarouselSectionProps = {
  productLines: CarouselDisplayProductLine[];
  eventViewName: ProductLineListSource;
  classNames: CarouselSectionClassNames;
};

export const TopSellerCarouselSection = ({
  productLines,
  eventViewName,
  classNames,
}: TopSellerCarouselSectionProps) => {
  const topSellerCards = productLines.map(toCardShape);
  const ref = useViewItemListOnVisible(topSellerCards, eventViewName);
  const productLinesViewRef = useProductLinesViewOnVisible(topSellerCards, eventViewName);

  return (
    <div className={classNames.category}>
      <div className={classNames.info}>
        <div className={classNames.titles}>
          <h3 className={classNames.title}>
            Browse <span className={classNames.accent}>popular</span> medications
          </h3>
        </div>
      </div>
      <HorizontalOverflow
        ref={ref}
        direction="right"
        classNames={{ scroller: classNames.products, button: classNames.button }}
      >
        <CategoryCard.TopSellers className={classNames.card} />
        {topSellerCards.map((productLine, index) => (
          <div
            key={productLine.id}
            ref={index === 0 ? productLinesViewRef : undefined}
            className={classNames.card}
          >
            <ProductLineCard
              productLine={productLine}
              eventViewName={eventViewName}
              eventCategoryId={null}
            />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

type CategoryCarouselSectionProps = {
  category: CarouselDisplayCategory;
  index: number;
  productLines: CarouselDisplayProductLine[];
  eventViewName: ProductLineListSource;
  classNames: CarouselSectionClassNames;
};

export const CategoryCarouselSection = ({
  category,
  index,
  productLines,
  eventViewName,
  classNames,
}: CategoryCarouselSectionProps) => {
  const categoryCarouselData = productLines.map(toCardShape);
  const ref = useViewItemListOnVisible(categoryCarouselData, `${eventViewName} - ${category.name}`);
  const productLinesViewRef = useProductLinesViewOnVisible(
    categoryCarouselData,
    eventViewName,
    category.id,
  );

  return (
    <div className={classNames.category}>
      <div className={classNames.info}>
        <div className={classNames.titles}>
          <h3 className={classNames.title}>{category.extended_name}</h3>
        </div>
        <PillButton
          size="s"
          variant="secondary"
          dest={urls.products.category({ params: { id: category.id } })}
        >
          View all
        </PillButton>
      </div>
      <HorizontalOverflow
        ref={ref}
        direction="right"
        classNames={{ scroller: classNames.products, button: classNames.button }}
      >
        <CategoryCardV2 category={category} index={index} className={classNames.card} />
        {categoryCarouselData.map((productLine, cardIndex) => (
          <div
            key={productLine.id}
            ref={cardIndex === 0 ? productLinesViewRef : undefined}
            className={classNames.card}
          >
            <ProductLineCard
              productLine={productLine}
              eventViewName={eventViewName}
              eventCategoryId={category.id}
            />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};
